import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Loader2, Mail, Plus, Copy, CheckCircle, X, Clock, AlertCircle } from 'lucide-react'
import type { GroupInvite } from './types'
import { formatDate } from './utils'

interface InvitesTabProps {
  invites: GroupInvite[]
  invitesLoading: boolean
  invitesError: string | null
  // Create invite form
  createEmail: string
  onCreateEmailChange: (value: string) => void
  createRole: 'member' | 'admin'
  onCreateRoleChange: (value: 'member' | 'admin') => void
  createLoading: boolean
  createdLink: string | null
  linkCopied: boolean
  onCreateInvite: () => void
  onCopyCreatedLink: () => void
  // Existing invites
  copiedInviteId: string | null
  onCopyInviteLink: (invite: GroupInvite) => void
  onRevokeInvite: (invite: GroupInvite) => void
}

export function InvitesTab({
  invites,
  invitesLoading,
  invitesError,
  createEmail,
  onCreateEmailChange,
  createRole,
  onCreateRoleChange,
  createLoading,
  createdLink,
  linkCopied,
  onCreateInvite,
  onCopyCreatedLink,
  copiedInviteId,
  onCopyInviteLink,
  onRevokeInvite,
}: InvitesTabProps) {
  // Note: pendingCount could be used for badge display if needed
  // const pendingCount = invites.filter(i => i.status === 'pending').length

  return (
    <div className="space-y-6">
      {/* Create Invite Card */}
      <Card>
        <CardHeader>
          <CardTitle>Create Invite</CardTitle>
          <CardDescription>Generate an invite link to share with new members</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="invite-email">Email (optional)</Label>
              <Input
                id="invite-email"
                placeholder="player@example.com"
                value={createEmail}
                onChange={(e) => onCreateEmailChange(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite-role">Role</Label>
              <select
                id="invite-role"
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={createRole}
                onChange={(e) => onCreateRoleChange(e.target.value as 'member' | 'admin')}
              >
                <option value="member">Member</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          </div>
          
          {createdLink && (
            <div className="rounded-md border bg-green-50 dark:bg-green-900/20 p-3 space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-green-700 dark:text-green-400">
                <CheckCircle className="h-4 w-4" />
                Invite link created!
              </div>
              <div className="flex items-center gap-2">
                <Input value={createdLink} readOnly className="bg-background font-mono text-xs" />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onCopyCreatedLink}
                >
                  {linkCopied ? (
                    <>
                      <CheckCircle className="h-4 w-4 mr-1.5 text-green-500" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-1.5" />
                      Copy
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          <Button
            type="button"
            disabled={createLoading}
            onClick={onCreateInvite}
          >
            {createLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Plus className="h-4 w-4 mr-2" />
                Create Invite
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Existing Invites Table */}
      <Card>
        <CardHeader>
          <CardTitle>Invite History</CardTitle>
          <CardDescription>
            {invites.length} {invites.length === 1 ? 'invite' : 'invites'} total
          </CardDescription>
        </CardHeader>
        <CardContent>
          {invitesLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : invitesError ? (
            <div className="rounded-md bg-destructive/10 border border-destructive/20 p-3 flex items-center gap-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4" />
              {invitesError}
            </div>
          ) : invites.length === 0 ? (
            <div className="text-center py-8">
              <Mail className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
              <p className="text-muted-foreground">No invites yet. Create one above to get started.</p>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Expires</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invites.map((inv) => (
                      <TableRow key={inv.inviteId}>
                        <TableCell className="font-medium">
                          {inv.email || <span className="text-muted-foreground italic">Open invite</span>}
                        </TableCell>
                        <TableCell>
                          <span className="capitalize">{inv.role}</span>
                        </TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full capitalize ${
                            inv.status === 'pending'
                              ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400'
                              : inv.status === 'accepted'
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                          }`}>
                            {inv.status === 'pending' && <Clock className="h-3 w-3" />}
                            {inv.status === 'accepted' && <CheckCircle className="h-3 w-3" />}
                            {inv.status === 'revoked' && <X className="h-3 w-3" />}
                            {inv.status}
                          </span>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDate(inv.createdAt)}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDate(inv.expiresAt)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {inv.status === 'pending' && inv.token && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => onCopyInviteLink(inv)}
                              >
                                {copiedInviteId === inv.inviteId ? (
                                  <CheckCircle className="h-4 w-4 text-green-500" />
                                ) : (
                                  <Copy className="h-4 w-4" />
                                )}
                              </Button>
                            )}
                            {inv.status === 'pending' && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => onRevokeInvite(inv)}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile Card View */}
              <div className="md:hidden space-y-3">
                {invites.map((inv) => (
                  <div key={inv.inviteId} className="rounded-lg border p-4 space-y-3">
                    {/* Header with email and status */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">
                          {inv.email || <span className="text-muted-foreground italic">Open invite</span>}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-muted-foreground capitalize">{inv.role}</span>
                          <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full capitalize ${
                            inv.status === 'pending'
                              ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400'
                              : inv.status === 'accepted'
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                          }`}>
                            {inv.status === 'pending' && <Clock className="h-3 w-3" />}
                            {inv.status === 'accepted' && <CheckCircle className="h-3 w-3" />}
                            {inv.status === 'revoked' && <X className="h-3 w-3" />}
                            {inv.status}
                          </span>
                        </div>
                      </div>
                      {inv.status === 'pending' && (
                        <div className="flex items-center gap-1 shrink-0">
                          {inv.token && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => onCopyInviteLink(inv)}
                            >
                              {copiedInviteId === inv.inviteId ? (
                                <CheckCircle className="h-4 w-4 text-green-500" />
                              ) : (
                                <Copy className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => onRevokeInvite(inv)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Dates */}
                    <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t">
                      <span>Created {formatDate(inv.createdAt)}</span>
                      <span>Expires {formatDate(inv.expiresAt)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
